// Recipes (M5, WP5.2): every live recipe with its active version, the drafts waiting for the
// baker's sign-off, what one batch costs, the quantities worth a second look and the
// allergens. Under "Set up": recipes are definitions the account writes and the baker signs
// off; what the kitchen makes each day is under Production.

import type { RecipeSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  Money,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@enp/ui';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus, ScrollText, Search, SearchX, TriangleAlert, X } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { usePageSearch } from '../../../shell/signals.js';
import { FilterSelect, LoadMoreFooter } from '../../../stock/components.js';
import { clean } from '../../../stock/queries.js';
import { AllergenChips, RecipeViews, useCan, useSearchText } from '../../../production/parts.js';
import {
  recipesQuery,
  recipesSearchSchema,
  type RecipesSearch,
} from '../../../production/queries.js';
import {
  isPersonCost,
  refusalText,
  sentence,
  STATEMENT_STALE,
  unitCostText,
} from '../../../production/words.js';

export const Route = createFileRoute('/_app/recipes/')({
  validateSearch: recipesSearchSchema,
  component: RecipesPage,
});

const TABLE_LABEL = 'Recipes';

const STATE_OPTIONS = [
  // Every draft the baker has to look at, changes to recipes in use included (UI5A-3).
  { value: 'draft', label: 'Waiting for sign-off' },
  { value: 'never_signed', label: 'Never signed off' },
  { value: 'active', label: 'In use (signed off)' },
  { value: 'none', label: 'Only retired versions' },
];

/**
 * What one batch costs and where the figure comes from (UI5A-8, UI5A-12): summed line by line
 * by the server, never a rounded unit cost × yield; a draft's cost says it isn't signed off.
 */
function CostCell({ r }: { r: RecipeSummary }) {
  const draft = r.costed_version?.status === 'draft';
  if (r.standard_cost_cents === null && r.batch_cost_cents === null) {
    return (
      <span className="flex flex-col items-end leading-tight">
        <span className="whitespace-nowrap text-sm text-muted">Not worked out</span>
        {r.cost_gap && (
          <span className="max-w-56 text-right text-xs text-muted">
            {sentence(refusalText(r.cost_gap.message))}
          </span>
        )}
      </span>
    );
  }
  return (
    <span className="flex flex-col items-end whitespace-nowrap leading-tight">
      {r.batch_cost_cents !== null && (
        <span className="text-fg">
          <Money cents={r.batch_cost_cents} className="font-medium" /> a batch
        </span>
      )}
      {r.standard_cost_cents !== null && (
        <span className={r.batch_cost_cents !== null ? 'text-xs text-muted' : 'text-fg'}>
          {unitCostText(r.standard_cost_cents, r.product.base_uom)}
          {isPersonCost(r.cost_source) ? ', set by a person' : ''}
        </span>
      )}
      {r.costed_version && (
        <span className="text-xs text-muted">
          {draft
            ? `From draft version ${r.costed_version.version_no}, not signed off`
            : `Version ${r.costed_version.version_no}, in use`}
        </span>
      )}
    </span>
  );
}

function columns(): ColumnDef<RecipeSummary, unknown>[] {
  const col = createColumnHelper<RecipeSummary>();
  return [
    col.display({
      id: 'product',
      header: 'Product',
      meta: { width: 'min-w-52 w-52' },
      cell: ({ row }) => (
        <Link
          to="/recipes/$recipeId"
          params={{ recipeId: row.original.id }}
          className="font-medium text-fg hover:underline"
        >
          {row.original.product.name}
        </Link>
      ),
    }),
    col.display({
      id: 'active',
      header: 'In use',
      meta: { width: 'w-36' },
      cell: ({ row }) => {
        const a = row.original.active_version;
        return a ? (
          <span className="flex flex-col leading-tight">
            <span className="text-fg">Version {a.version_no}</span>
            <span className="text-xs text-muted">
              Signed off <AccountDate value={a.signed_off_at} />
            </span>
          </span>
        ) : (
          <span className="text-muted">No signed-off version</span>
        );
      },
    }),
    col.display({
      id: 'signoff',
      header: 'Sign-off',
      meta: { width: 'w-56' },
      cell: ({ row }) => {
        const n = row.original.draft_count;
        return n > 0 ? (
          <span className="flex flex-col items-start gap-0.5">
            <StatusBadge status="warning">
              {n === 1 ? 'Draft to sign off' : `${formatCount(n)} drafts to sign off`}
            </StatusBadge>
            <span className="text-xs text-muted">
              {row.original.active_version
                ? `Version ${row.original.latest_version_no}: a change to the one in use`
                : `Version ${row.original.latest_version_no}: never signed off`}
            </span>
          </span>
        ) : (
          <span className="text-muted">Nothing waiting</span>
        );
      },
    }),
    col.display({
      id: 'cost',
      header: 'Standard cost',
      meta: { align: 'right', width: 'w-52' },
      cell: ({ row }) => <CostCell r={row.original} />,
    }),
    col.display({
      id: 'checks',
      header: 'To check',
      meta: { width: 'w-32' },
      cell: ({ row }) => {
        const n = row.original.warning_count;
        return n > 0 ? (
          <span className="inline-flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-fg">
              <TriangleAlert aria-hidden className="size-4 text-state-warning" />
              {n === 1 ? '1 to check' : `${formatCount(n)} to check`}
            </span>
            {row.original.statement_out_of_date && (
              <span className="text-xs text-muted">{STATEMENT_STALE}</span>
            )}
          </span>
        ) : (
          <span className="text-muted">None</span>
        );
      },
    }),
    col.display({
      id: 'allergens',
      header: 'Allergens',
      meta: { className: 'hidden lg:table-cell' },
      cell: ({ row }) => (
        <AllergenChips
          allergens={row.original.product.allergens}
          ingredients={row.original.ingredient_allergens}
          short
        />
      ),
    }),
  ];
}

function RecipesPage() {
  useDocumentTitle('Recipes');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canEdit = useCan('recipe.edit', true);
  const setSearch = (patch: Partial<RecipesSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  const [text, setText] = useSearchText(search.q, (q) => setSearch({ q }));
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);

  const list = useInfiniteQuery({ ...recipesQuery(search), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total;
  const filtered = Boolean(search.q || search.state);
  // No recipe at all: the empty state's "Write a recipe" is the one action (UI8C-6).
  const noneYet =
    !filtered && list.data !== undefined && !list.isPlaceholderData && items.length === 0;

  const clearAll = () => {
    setText('');
    void navigate({ search: {} });
  };

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No recipes match"
      // Says only what is set: a search, a filter, or both (UI5A-111).
      description={
        search.q && search.state
          ? `No recipe for a product called “${search.q}” with this filter.`
          : search.q
            ? `No recipe for a product called “${search.q}”.`
            : 'No recipe fits this filter.'
      }
      action={
        <Button variant="secondary" icon={<X aria-hidden />} onClick={clearAll}>
          {search.q && search.state
            ? 'Clear search and filter'
            : search.q
              ? 'Clear the search'
              : 'Clear the filter'}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={ScrollText}
      title="No recipes yet"
      description="A recipe says what one batch of a product is made of. The catalogue import brings recipes in as drafts; you can also write one here. The baker signs each one off before it’s used."
      action={
        canEdit ? (
          <Button asChild>
            <Link to="/recipes/new">
              <Plus aria-hidden />
              Write a recipe
            </Link>
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Recipes"
        description="What one batch of each product is made of. The baker signs off each version before production uses it; until one is signed off, the cost comes from the latest draft."
        actions={
          canEdit &&
          !noneYet && (
            <Button asChild>
              <Link to="/recipes/new">
                <Plus aria-hidden />
                New recipe
              </Link>
            </Button>
          )
        }
      />
      <RecipeViews current="recipes" />

      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Recipe search"
      >
        <div className="relative w-full max-w-64">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search recipes"
            aria-keyshortcuts="/"
            placeholder="Search by product name"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="pl-8"
          />
        </div>
        <FilterSelect
          label="Sign-off"
          value={search.state}
          allLabel="Every recipe"
          options={STATE_OPTIONS}
          onChange={(v) => setSearch({ state: v as RecipesSearch['state'] })}
        />
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clearAll}>
            Clear
          </Button>
        )}
      </div>

      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The recipes"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns()}
          data={items}
          loading={list.isPending}
          empty={empty}
          filterable={false}
          getRowId={(r) => r.id}
          onRowActivate={(r) =>
            void navigate({ to: '/recipes/$recipeId', params: { recipeId: r.id } })
          }
          footer={
            !list.isPending && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                total={total}
                noun={['recipe', 'recipes']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
        />
      )}
    </>
  );
}
